import './PageToolbar.scss';
import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);

const FilterIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
  </svg>
);

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);

const CloseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

export default function PageToolbar({
  onAdd,
  addLabel,
  filters,
  onFilterChange,
  activeFilters = [],
  searchValue,
  onSearchChange,
  searchPlaceholder,
  extraButtons,
  onClearAll,
}) {
  const { t } = useTranslation();
  const resolvedAddLabel = addLabel || t('common:add');
  const resolvedSearchPlaceholder = searchPlaceholder || t('common:search');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filterRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) {
        setFiltersOpen(false);
      }
    };
    if (filtersOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [filtersOpen]);

  const hasActiveFilters = activeFilters.length > 0;
  const hasAnythingActive = hasActiveFilters || (searchValue && searchValue.length > 0);

  return (
    <div className="PageToolbar">
      {/* Row 1: Actions + extra */}
      <div className="PageToolbar__row">
        <div className="PageToolbar__actions">
          {onAdd && (
            <button className="PageToolbar__add-btn" onClick={onAdd} title={resolvedAddLabel}>
              <PlusIcon />
              <span>{resolvedAddLabel}</span>
            </button>
          )}

          {filters && filters.length > 0 && (
            <div className="PageToolbar__filter-wrapper" ref={filterRef}>
              <button
                className={`PageToolbar__filter-btn ${hasActiveFilters ? 'has-active' : ''}`}
                onClick={() => setFiltersOpen(!filtersOpen)}
              >
                <FilterIcon />
                <span>{t('common:status')}</span>
                {hasActiveFilters && <span className="PageToolbar__filter-count">{activeFilters.length}</span>}
              </button>

              {filtersOpen && (
                <div className="PageToolbar__filter-dropdown">
                  {filters.map((f) => (
                    <button
                      key={f.value}
                      className={`PageToolbar__filter-item ${activeFilters.includes(f.value) ? 'active' : ''}`}
                      onClick={() => onFilterChange(f.value)}
                    >
                      <span className="PageToolbar__filter-check">
                        {activeFilters.includes(f.value) ? '✓' : ''}
                      </span>
                      {f.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {extraButtons && (
            <div className="PageToolbar__extra">
              {extraButtons}
            </div>
          )}
        </div>
      </div>

      {/* Row 2: Search */}
      {onSearchChange && (
        <div className="PageToolbar__search-row">
          <div className="PageToolbar__search">
            {onClearAll && hasAnythingActive ? (
              <button
                className="PageToolbar__search-icon-btn PageToolbar__search-icon-btn--clear"
                onClick={onClearAll}
                title={t('common:clearFilters')}
              >
                <CloseIcon />
              </button>
            ) : (
              <SearchIcon />
            )}
            <input
              type="text"
              placeholder={resolvedSearchPlaceholder}
              value={searchValue || ''}
              onChange={(e) => onSearchChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape' && onClearAll) {
                  onClearAll();
                  e.target.blur();
                }
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
